import React, { useState } from "react";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, CalendarDays, GraduationCap, ClipboardList,
  FolderOpen, Tags, Receipt, Menu, X, LogOut, Compass,
} from "lucide-react";

export const PORTAL_GROUPS = [
  {
    label: null,
    items: [
      { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
      { id: "appointments", label: "Appointments", icon: CalendarDays },
    ],
  },
  {
    label: "My Learning",
    items: [
      { id: "courses", label: "Courses", icon: GraduationCap },
      { id: "checkins", label: "Wellbeing Check-Ins", icon: ClipboardList },
      { id: "resources", label: "Resources", icon: FolderOpen },
    ],
  },
  {
    label: "Sessions",
    items: [
      { id: "plans", label: "Sessions & Packages", icon: Tags },
      { id: "invoices", label: "Invoices", icon: Receipt },
    ],
  },
  {
    label: "Explore",
    items: [
      { id: "discover", label: "Discover More", icon: Compass },
    ],
  },
];

function SidebarContent({ active, onSelect, onLogout }) {
  return (
    <div className="flex h-full flex-col border-r border-gold-200/50 bg-white/70 backdrop-blur-xl">
      <div className="px-5 py-7">
        <Logo size="h-14" />
      </div>
      <nav className="flex-1 space-y-6 overflow-y-auto px-3 pb-4">
        {PORTAL_GROUPS.map((group) => (
          <div key={group.label || "primary"}>
            {group.label && (
              <div className="mb-2 px-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-navy-400">
                {group.label}
              </div>
            )}
            <div className="space-y-1">
              {group.items.map((item) => {
                const isActive = active === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => onSelect(item.id)}
                    data-testid={`portal-nav-${item.id}`}
                    className={`group flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors duration-200 ${
                      isActive ? "bg-gold-50 text-gold-700" : "text-navy-500 hover:bg-navy-50 hover:text-navy-900"
                    }`}
                  >
                    <item.icon
                      className={`h-[18px] w-[18px] ${isActive ? "text-gold-600" : "text-navy-400 group-hover:text-navy-700"}`}
                      strokeWidth={2}
                    />
                    {item.label}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </nav>
      <div className="border-t border-gold-200/50 p-4">
        <button
          onClick={onLogout}
          data-testid="portal-logout-button"
          className="mb-3 flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-navy-500 transition-colors duration-200 hover:bg-navy-50 hover:text-navy-900"
        >
          <LogOut className="h-[18px] w-[18px] text-navy-400" /> Sign out
        </button>
        <p className="px-1 text-xs leading-relaxed text-navy-400">
          A secure space for your wellbeing journey with Resilience Community Collective.
        </p>
      </div>
    </div>
  );
}

export default function PortalShell({ user, active, onSelect, onLogout, children }) {
  const [open, setOpen] = useState(false);
  const select = (id) => { onSelect(id); setOpen(false); };

  return (
    <div className="min-h-screen bg-ivory" data-testid="client-portal">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-72 lg:block">
        <SidebarContent active={active} onSelect={select} onLogout={onLogout} />
      </aside>

      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-navy-900/40" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-72 animate-fade-up">
            <SidebarContent active={active} onSelect={select} onLogout={onLogout} />
          </div>
        </div>
      )}

      <div className="lg:pl-72">
        <div className="sticky top-0 z-20 flex items-center justify-between border-b border-navy-900/5 bg-white/90 px-4 py-3 backdrop-blur sm:px-8">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setOpen(!open)} data-testid="portal-menu-toggle">
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
            <Logo size="h-9" className="lg:hidden" />
          </div>
          <div className="text-sm text-navy-500">
            Hi, <span className="font-semibold text-navy-900">{user?.name}</span> <span aria-hidden>🌿</span>
          </div>
        </div>

        <main key={active} className="mx-auto max-w-6xl animate-fade-up px-4 py-6 sm:px-8 sm:py-10">
          {children}
        </main>
      </div>
    </div>
  );
}
